import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { MdLocalOffer, MdStore, MdEvent, MdSync, MdSchedule, MdHistory } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { fetchRetailerDiscounts } from '../../../services/Http/Api';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TableCellWithIcon from '../../../shared/layout/tables/TableCellWithIcon';
import { formatDiscountBadge } from '../../../utils/discountDisplay';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';

// Counters and the discount size read best highest-first; names, dates and
// status still start ascending.
const DESC_FIRST_FIELDS = new Set(['discount', 'shows_count', 'views_count', 'clicks_count']);

const RetailerDiscounts = () => {
    const { t } = useTranslation();
    const defaultPerPage = 10;
    const [discounts, setDiscounts] = useState([]);
    const [summary, setSummary] = useState({ active: 0, upcoming: 0, expired: 0 });
    const [meta, setMeta] = useState({ current_page: 1, per_page: defaultPerPage, total: 0, last_page: 1 });
    const [statusFilter, setStatusFilter] = useState('all');
    // Sorting is server-side: the table only ever holds one page of rows.
    const [sort, setSort] = useState({ by: 'end_at', direction: 'asc' });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async ({ page, perPage, status, sortBy, sortDirection }) => {
        setLoading(true);
        try {
            const response = await fetchRetailerDiscounts({
                page,
                per_page: perPage,
                status: status === 'all' ? undefined : status,
                sort_by: sortBy,
                sort_direction: sortDirection,
            });
            setDiscounts(response.data || []);
            setSummary(response.summary || { active: 0, upcoming: 0, expired: 0 });
            setMeta(response.meta || meta);
        } catch (error) {
            toast.error(t('RetailerDiscounts.error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load({
            page: 1,
            perPage: defaultPerPage,
            status: statusFilter,
            sortBy: sort.by,
            sortDirection: sort.direction,
        });
    }, [statusFilter, sort, load]);

    const handlePageChange = (nextPage) => {
        if (nextPage < 1 || nextPage > meta.last_page) return;
        load({
            page: nextPage,
            perPage: meta.per_page,
            status: statusFilter,
            sortBy: sort.by,
            sortDirection: sort.direction,
        });
    };

    // Re-queries from page 1 so the sort covers every discount, not just the
    // rows currently on screen.
    //
    // react-data-table carries the previous direction over to a newly clicked
    // column instead of resetting it, which made the first click on a counter
    // land on the thousands of zero rows. Pick the direction ourselves for a
    // new column; keep the table's own toggle when the column is unchanged.
    const handleSort = (column, direction) => {
        const field = column.sortField || 'end_at';

        setSort({
            by: field,
            direction: field === sort.by
                ? direction
                : (DESC_FIRST_FIELDS.has(field) ? 'desc' : 'asc'),
        });
    };

    const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
    const tableStyles = useMemo(() => ({
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                minHeight: '48px',
                backgroundColor: '#f9fafb',
                borderBottomWidth: '1px',
                borderBottomColor: '#e5e7eb',
            },
        },
        headCells: {
            style: {
                color: '#64748b',
                fontSize: '0.72rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.045em',
            },
        },
        rows: {
            style: {
                minHeight: '62px',
                borderBottomColor: '#f1f5f9',
                '&:not(:last-of-type)': { borderBottomWidth: '1px' },
            },
            highlightOnHoverStyle: {
                backgroundColor: '#faf7fb',
                cursor: 'default',
            },
        },
        cells: {
            style: {
                color: '#334155',
                fontSize: '0.875rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
            },
        },
        pagination: {
            style: {
                minHeight: '56px',
                borderTopColor: '#f1f5f9',
                color: '#64748b',
            },
        },
    }), []);

    const columns = useMemo(() => [
        {
            id: 'product_name',
            name: t('RetailerDiscounts.table.product'),
            sortField: 'product_name',
            selector: (row) => row.product_name,
            sortable: true,
            cell: (row) => <strong>{truncate(row.product_name, 20)}</strong>,
        },
        {
            id: 'venue_name',
            name: t('RetailerDiscounts.table.venue'),
            sortField: 'venue_name',
            selector: (row) => row.venue_name || t('RetailerDiscounts.table.global'),
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdStore size={18} color="rgba(130, 22, 146, 0.8)" />
                    {row.venue_name || t('RetailerDiscounts.table.global')}
                </TableCellWithIcon>
            ),
        },
        {
            id: 'discount',
            name: t('RetailerDiscounts.table.discount'),
            sortField: 'discount',
            selector: (row) => row.discount_percentage ?? row.discount_value,
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdLocalOffer size={18} color="rgba(130, 22, 146, 0.8)" />
                    <DiscountBadge>-{formatDiscountBadge(row)}</DiscountBadge>
                </TableCellWithIcon>
            ),
            right: true,
        },
        {
            id: 'end_at',
            name: t('RetailerDiscounts.table.end'),
            sortField: 'end_at',
            selector: (row) => new Date(row.end_at),
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdEvent size={18} color="rgba(130, 22, 146, 0.8)" />
                    {new Intl.DateTimeFormat().format(new Date(row.end_at))}
                </TableCellWithIcon>
            ),
        },
        {
            id: 'status',
            name: t('RetailerDiscounts.table.status'),
            sortField: 'status',
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <StatusBadge $status={row.status}>
                    {t(`RetailerDiscounts.status.${row.status}`)}
                </StatusBadge>
            ),
        },
        {
            id: 'shows_count',
            name: t('RetailerDiscounts.table.shows'),
            sortField: 'shows_count',
            selector: (row) => row.shows_count ?? 0,
            sortable: true,
            right: true,
            cell: (row) => numberFormatter.format(row.shows_count ?? 0),
        },
        {
            id: 'views_count',
            name: t('RetailerDiscounts.table.views'),
            sortField: 'views_count',
            selector: (row) => row.views_count ?? 0,
            sortable: true,
            right: true,
            cell: (row) => numberFormatter.format(row.views_count ?? 0),
        },
        {
            id: 'clicks_count',
            name: t('RetailerDiscounts.table.clicks'),
            sortField: 'clicks_count',
            selector: (row) => row.clicks_count ?? 0,
            sortable: true,
            right: true,
            cell: (row) => numberFormatter.format(row.clicks_count ?? 0),
        },
    ], [numberFormatter, t]);

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('RetailerDiscounts.title')}</PageTitle>
                    <Subtitle>{t('RetailerDiscounts.subtitle')}</Subtitle>
                </div>
                <CatalogLink to="/catalog-import">
                    <MdSync size={18} aria-hidden="true" />
                    {t('RetailerDiscounts.manageCatalog')}
                </CatalogLink>
            </PageHeader>

            <PageContent>
                <StatsGrid>
                    <StatCard
                        icon={<MdLocalOffer />}
                        title={t('RetailerDiscounts.summary.active')}
                        value={numberFormatter.format(summary.active || 0)}
                        color="success"
                    />
                    <StatCard
                        icon={<MdSchedule />}
                        title={t('RetailerDiscounts.summary.upcoming')}
                        value={numberFormatter.format(summary.upcoming || 0)}
                        color="warning"
                    />
                    <StatCard
                        icon={<MdHistory />}
                        title={t('RetailerDiscounts.summary.expired')}
                        value={numberFormatter.format(summary.expired || 0)}
                        color="neutral"
                    />
                </StatsGrid>

                <TableSection>
                    <TableToolbar>
                        <div>
                            <SectionTitle>{t('RetailerDiscounts.tableTitle')}</SectionTitle>
                            <ResultsCount>
                                {t('RetailerDiscounts.resultsCount', { count: numberFormatter.format(meta.total || 0) })}
                            </ResultsCount>
                        </div>
                        <StatusFilters role="group" aria-label={t('RetailerDiscounts.filterLabel')}>
                            {['all', 'active', 'upcoming', 'expired'].map((status) => (
                                <FilterButton
                                    key={status}
                                    type="button"
                                    $active={statusFilter === status}
                                    aria-pressed={statusFilter === status}
                                    onClick={() => setStatusFilter(status)}
                                >
                                    {status === 'all' ? t('RetailerDiscounts.filters.all') : t(`RetailerDiscounts.status.${status}`)}
                                </FilterButton>
                            ))}
                        </StatusFilters>
                    </TableToolbar>

                    <TablePanel>
                        {loading ? (
                            <Placeholder role="status">{t('common.loading')}</Placeholder>
                        ) : discounts.length === 0 ? (
                            <Placeholder>{t('RetailerDiscounts.empty')}</Placeholder>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={discounts}
                                pagination
                                paginationServer
                                paginationTotalRows={meta.total}
                                paginationPerPage={meta.per_page}
                                onChangePage={handlePageChange}
                                sortServer
                                onSort={handleSort}
                                // Remounted per sort so these initial values re-apply and the
                                // header arrow reflects the direction we requested.
                                key={`${sort.by}-${sort.direction}`}
                                defaultSortFieldId={sort.by}
                                defaultSortAsc={sort.direction === 'asc'}
                                progressPending={loading}
                                highlightOnHover
                                customStyles={tableStyles}
                                noDataComponent={<Placeholder>{t('RetailerDiscounts.empty')}</Placeholder>}
                            />
                        )}
                    </TablePanel>
                </TableSection>
            </PageContent>
        </PageContainer>
    );
};

export default RetailerDiscounts;

const Subtitle = styled.p`
    margin: 0.35rem 0 0;
    color: ${({ theme }) => theme.colors.textMuted};
    flex: 1;
`;

const CatalogLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 42px;
    padding: 0.65rem 1rem;
    border: 1px solid ${({ theme }) => theme.colors.brand};
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.brand};
    color: ${({ theme }) => theme.colors.textInverse};
    text-decoration: none;
    font-weight: 650;
    white-space: nowrap;
    transition: background 0.15s ease, box-shadow 0.15s ease;

    &:hover, &:focus-visible {
        background: #701a82;
        color: #fff;
        box-shadow: 0 4px 12px rgba(112, 26, 130, 0.2);
    }

    @media (max-width: 640px) {
        width: 100%;
    }
`;

const TableSection = styled.section`
    margin-top: 1.5rem;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 14px;
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.045);
`;

const TableToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const SectionTitle = styled.h2`
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 1rem;
    font-weight: 700;
`;

const ResultsCount = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.82rem;
`;

const TablePanel = styled.div`
    overflow-x: auto;

    & > div {
        min-width: 760px;
    }
`;

const StatusFilters = styled.div`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
    padding: 0.25rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 11px;
    background: ${({ theme }) => theme.colors.surfaceAlt};

    @media (max-width: 760px) {
        width: fit-content;
        max-width: 100%;
    }
`;

const FilterButton = styled.button`
    padding: 0.48rem 0.72rem;
    border-radius: 8px;
    border: 1px solid transparent;
    background: ${({ theme, $active }) => ($active ? theme.colors.surface : 'transparent')};
    color: ${({ theme, $active }) => ($active ? theme.colors.brand : theme.colors.textMuted)};
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 650;
    white-space: nowrap;
    transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

    ${({ $active }) => $active && `box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);`}

    &:hover, &:focus-visible {
        color: ${({ theme }) => theme.colors.brand};
        background: ${({ theme }) => theme.colors.surface};
        outline: none;
    }
`;

const Placeholder = styled.div`
    padding: 3.5rem 1.5rem;
    text-align: center;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.9rem;
`;

const DiscountBadge = styled.span`
    padding: 0.2rem 0.6rem;
    border-radius: 6px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
`;

const StatusBadge = styled.span`
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    font-size: 0.875rem;
    text-transform: capitalize;
    background: ${({ $status }) => {
        switch ($status) {
            case 'active':
                return 'rgba(76, 175, 80, 0.15)';
            case 'upcoming':
                return 'rgba(255, 193, 7, 0.15)';
            default:
                return 'rgba(158, 158, 158, 0.15)';
        }
    }};
    color: ${({ $status }) => {
        switch ($status) {
            case 'active':
                return '#2e7d32';
            case 'upcoming':
                return '#f57c00';
            default:
                return '#616161';
        }
    }};
`;
const truncate = (value, maxLength = 80) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};
